import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { track } from '../lib/track';

/*
 * MemberAuthContext — the source of truth for the currently signed-in
 * N3URO Access member. Separate from AdminAuthContext (admin dashboard).
 *
 *   member = null       → still checking session
 *   member = false      → signed out
 *   member = { … }      → signed in (id, email, first_name, tier, …)
 */
const MemberAuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

export function formatApiError(detail, fallback = 'Something went wrong.') {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const MemberAuthProvider = ({ children }) => {
  const [member, setMember] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setMember(data);
      return data;
    } catch {
      setMember(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const register = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/api/auth/register`, payload, { withCredentials: true });
    track('access_register', 'free-access-profile');
    setMember(data);
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(
      `${API}/api/auth/login`,
      { email, password },
      { withCredentials: true },
    );
    setMember(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.warn('Logout request failed; clearing local session anyway:', err);
    }
    setMember(false);
  }, []);

  const forgot = useCallback(async (email) => {
    await axios.post(`${API}/api/auth/forgot-password`, { email }, { withCredentials: true });
  }, []);

  const reset = useCallback(async (token, password) => {
    const { data } = await axios.post(
      `${API}/api/auth/reset-password`,
      { token, password },
      { withCredentials: true },
    );
    setMember(data);
    return data;
  }, []);

  return (
    <MemberAuthContext.Provider value={{ member, refresh, register, login, logout, forgot, reset }}>
      {children}
    </MemberAuthContext.Provider>
  );
};

export const useMember = () => {
  const ctx = useContext(MemberAuthContext);
  if (!ctx) throw new Error('useMember must be used within MemberAuthProvider');
  return ctx;
};

/* Convenience helpers for tier-gated UI. */
export const TIER_ORDER = ['FREE_ACCESS', 'ACCESS_PLUS', 'FAMILY_PLUS', 'PREMIUM_PLUS'];
export const TIER_LABEL = {
  FREE_ACCESS: 'Free',
  ACCESS_PLUS: 'Access+',
  FAMILY_PLUS: 'Family+',
  PREMIUM_PLUS: 'Premium+',
};
export const hasTier = (member, minimum) => {
  if (!member) return false;
  return TIER_ORDER.indexOf(member.tier || 'FREE_ACCESS') >= TIER_ORDER.indexOf(minimum);
};
